import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import IconBox from 'components/icons/IconBox';
import MiniStatistics from 'components/card/MiniStatistics';
import Button from 'components/ui/Button';
import ServiceItemModal from 'features/services/ServiceItemModal';
import {
  useCreateServiceItem,
  useServiceItems,
  useToggleServiceItem,
  useUpdateServiceItem,
} from 'hooks/useServiceItems';
import React, { useMemo, useState } from 'react';
import {
  MdAdd,
  MdCheckCircle,
  MdInventory2,
  MdMoreVert,
  MdPauseCircle,
  MdSearch,
} from 'react-icons/md';
import { CreateServiceItemDto, EstadoServicio, ServiceItem } from 'types/models';

export default function ServicesView() {
  const toast = useToast();

  const { data: services = [], isLoading, isError } = useServiceItems();
  const createMutation  = useCreateServiceItem();
  const updateMutation  = useUpdateServiceItem();
  const toggleMutation  = useToggleServiceItem();

  const [searchQuery,      setSearchQuery]      = useState('');
  const [filterEstado,     setFilterEstado]      = useState<'todos' | EstadoServicio>('todos');
  const [isModalOpen,      setIsModalOpen]       = useState(false);
  const [selectedService,  setSelectedService]   = useState<ServiceItem | null>(null);

  const brandColor  = useColorModeValue('brand.500', 'white');
  const boxBg       = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const tableBg     = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const hoverBg     = useColorModeValue('gray.50', 'navy.700');
  const theadBg     = useColorModeValue('gray.50', 'navy.750');

  const totalActivos   = services.filter(s => s.status === EstadoServicio.ACTIVO).length;
  const totalInactivos = services.filter(s => s.status === EstadoServicio.INACTIVO).length;

  const filtered = useMemo(() => {
    let list = services;
    if (filterEstado !== 'todos') list = list.filter(s => s.status === filterEstado);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        s =>
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          (s.description?.toLowerCase().includes(q) ?? false)
      );
    }
    return list;
  }, [services, filterEstado, searchQuery]);

  const handleOpenAdd = () => {
    setSelectedService(null);
    setIsModalOpen(true);
  };

  const handleEdit = (service: ServiceItem) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleToggle = async (service: ServiceItem) => {
    const activate = service.status === EstadoServicio.INACTIVO;
    try {
      await toggleMutation.mutateAsync({ id: service.id, activate });
      toast({
        title: activate ? 'Servicio activado' : 'Servicio desactivado',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message ?? 'Intente nuevamente',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleSave = async (data: CreateServiceItemDto) => {
    try {
      if (selectedService) {
        await updateMutation.mutateAsync({ id: selectedService.id, payload: data });
        toast({ title: 'Servicio actualizado', status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
      } else {
        await createMutation.mutateAsync(data);
        toast({ title: 'Servicio creado', status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
      }
      setIsModalOpen(false);
    } catch (err: any) {
      toast({
        title: 'Error al guardar',
        description: err?.response?.data?.message ?? 'Intente nuevamente',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap='20px' mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdInventory2} color={brandColor} />}
            />
          }
          name='Total Servicios'
          value={isLoading ? '—' : String(services.length)}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg='green.50'
              icon={<Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />}
            />
          }
          name='Activos'
          value={isLoading ? '—' : String(totalActivos)}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg='gray.100'
              icon={<Icon w='32px' h='32px' as={MdPauseCircle} color='gray.400' />}
            />
          }
          name='Inactivos'
          value={isLoading ? '—' : String(totalInactivos)}
        />
      </SimpleGrid>

      {/* Header */}
      <Flex justify='space-between' align='center' mb='20px' flexWrap='wrap' gap='12px'>
        <Flex direction='column'>
          <Text color={textColor} fontSize='2xl' fontWeight='800'>
            Servicios e Inventario
          </Text>
          <Text color='secondaryGray.600' fontSize='sm'>
            Catálogo de servicios clínicos y tarifario
          </Text>
        </Flex>
        <Flex gap='10px' align='center' flexWrap='wrap'>
          {/* Filtro estado */}
          <Flex gap='6px'>
            {(['todos', EstadoServicio.ACTIVO, EstadoServicio.INACTIVO] as const).map(opt => (
              <Button
                key={opt}
                size='sm'
                variant={filterEstado === opt ? 'solid' : 'outline'}
                colorScheme={opt === EstadoServicio.ACTIVO ? 'green' : opt === EstadoServicio.INACTIVO ? 'gray' : 'brand'}
                onClick={() => setFilterEstado(opt)}
                textTransform='capitalize'
              >
                {opt === 'todos' ? 'Todos' : opt}
              </Button>
            ))}
          </Flex>
          {/* Búsqueda */}
          <InputGroup w={{ base: 'full', md: '260px' }}>
            <InputLeftElement pointerEvents='none' h='40px'>
              <Icon as={MdSearch} color='secondaryGray.600' w='18px' h='18px' />
            </InputLeftElement>
            <Input
              pl='40px'
              h='40px'
              bg={tableBg}
              border='1px solid'
              borderColor={borderColor}
              borderRadius='12px'
              fontSize='sm'
              placeholder='Buscar servicio...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
            />
          </InputGroup>
          <Button leftIcon={<Icon as={MdAdd} />} onClick={handleOpenAdd}>
            Nuevo Servicio
          </Button>
        </Flex>
      </Flex>

      {/* Error */}
      {isError && (
        <Alert status='error' borderRadius='16px' mb='20px'>
          <AlertIcon />
          Error al cargar servicios. Verifique su conexión e intente nuevamente.
        </Alert>
      )}

      {/* Tabla */}
      {isLoading ? (
        <Stack spacing='3'>
          {[...Array(5)].map((_, i) => <Skeleton key={i} h='52px' borderRadius='12px' />)}
        </Stack>
      ) : (
        <Box bg={tableBg} borderRadius='16px' border='1px solid' borderColor={borderColor} overflow='hidden' boxShadow='sm'>
          <Table variant='simple' size='md'>
            <Thead bg={theadBg}>
              <Tr>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider'>Código</Th>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider'>Nombre</Th>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider'>Descripción</Th>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider' isNumeric>Precio Base</Th>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider'>Estado</Th>
                <Th py='18px' color='secondaryGray.600' fontSize='13px' letterSpacing='wider' w='60px' />
              </Tr>
            </Thead>
            <Tbody>
              {filtered.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign='center' py='48px' color='secondaryGray.500' fontSize='md'>
                    No hay servicios que coincidan con la búsqueda.
                  </Td>
                </Tr>
              ) : (
                filtered.map(service => (
                  <Tr key={service.id} _hover={{ bg: hoverBg }} transition='background 0.15s'>
                    <Td py='18px'>
                      <Text fontSize='sm' fontWeight='700' fontFamily='mono' color='brand.500'>
                        {service.code}
                      </Text>
                    </Td>
                    <Td py='18px'>
                      <Text fontSize='md' fontWeight='600'>{service.name}</Text>
                    </Td>
                    <Td py='18px' maxW='240px'>
                      <Tooltip label={service.description ?? ''} isDisabled={!service.description} placement='top'>
                        <Text fontSize='sm' color='secondaryGray.600' noOfLines={1}>
                          {service.description || '—'}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td py='18px' isNumeric>
                      <Text fontSize='md' fontWeight='700' color='green.600'>
                        ${Number(service.basePrice).toFixed(2)}
                      </Text>
                    </Td>
                    <Td py='18px'>
                      <Badge
                        colorScheme={service.status === EstadoServicio.ACTIVO ? 'green' : 'gray'}
                        variant='subtle'
                        borderRadius='full'
                        px='3'
                        py='1'
                        fontSize='sm'
                        textTransform='capitalize'
                      >
                        {service.status}
                      </Badge>
                    </Td>
                    <Td py='18px'>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<Icon as={MdMoreVert} />}
                          variant='ghost'
                          size='md'
                          aria-label='Acciones'
                          borderRadius='8px'
                        />
                        <MenuList fontSize='sm' minW='160px'>
                          <MenuItem onClick={() => handleEdit(service)}>
                            Editar
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleToggle(service)}
                            color={service.status === EstadoServicio.ACTIVO ? 'orange.500' : 'green.500'}
                          >
                            {service.status === EstadoServicio.ACTIVO ? 'Desactivar' : 'Activar'}
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      <ServiceItemModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        service={selectedService}
        isSaving={isSaving}
      />
    </Box>
  );
}
